import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { FormSection } from '../ui/FormSection';
import type { DistanceUnit } from '@wineryxplorer/shared';

/** `null` is the stored-nothing state: follow the device / browser. */
type DistanceUnitChoice = DistanceUnit | null;

const OPTIONS: { value: DistanceUnitChoice; label: string }[] = [
  { value: null, label: 'Automatic' },
  { value: 'mi', label: 'Miles' },
  { value: 'km', label: 'Kilometers' },
];

interface DisplaySectionProps {
  /** Undefined when the user has never chosen — rendered as Automatic. */
  distanceUnit: DistanceUnit | undefined;
}

export const DisplaySection = ({ distanceUnit: initialDistanceUnit }: DisplaySectionProps) => {
  const initialChoice: DistanceUnitChoice = initialDistanceUnit ?? null;
  const [distanceUnit, setDistanceUnit] = useState<DistanceUnitChoice>(initialChoice);
  const utils = trpc.useUtils();

  const updateDistanceUnit = trpc.user.updateProfile.useMutation({
    onSuccess: () => {
      utils.user.getMe.invalidate();
    },
    onError: () => {
      setDistanceUnit(initialChoice);
    },
  });

  const handleDistanceUnitChange = (next: DistanceUnitChoice) => {
    if (next === distanceUnit) return;
    setDistanceUnit(next);
    // null clears the stored choice on the server; see the router's input note.
    updateDistanceUnit.mutate({ distanceUnit: next });
  };

  return (
    <FormSection title="Display">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-900">Distance units</p>
          <p className="text-xs text-gray-500 mt-0.5">
            How distances are shown throughout the app. Automatic follows your device’s
            measurement setting.
          </p>
        </div>
        <div
          role="radiogroup"
          aria-label="Distance units"
          className="inline-flex rounded-md border border-gray-300 bg-gray-50 overflow-hidden flex-shrink-0"
        >
          {OPTIONS.map((option, index) => (
            <button
              key={option.label}
              type="button"
              role="radio"
              aria-checked={distanceUnit === option.value}
              onClick={() => handleDistanceUnitChange(option.value)}
              disabled={updateDistanceUnit.isPending}
              className={`px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                index > 0 ? 'border-l border-gray-300' : ''
              } ${
                distanceUnit === option.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {updateDistanceUnit.error && (
        <p className="text-sm text-red-600 mt-2">{updateDistanceUnit.error.message}</p>
      )}
    </FormSection>
  );
};
